import { CryoDTO, SoakingDTO } from 'business/models/Models';
import CryoprotectantService from 'business/services/CryoprotectantService';
import SoakingService from 'business/services/SoakingService';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import React, { useEffect } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { useCryoprotectants } from 'redux/hooks/synchrotron';
import {
  deleteCryoprotectants,
  setAllcryoprotectants,
  updateCryoprotectants,
} from 'redux/reducers/cryoprotectantReducer';
import {
  deleteSoakings,
  setAllSoakings,
  updateSoakings,
} from 'redux/reducers/soakingReducer';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { getNewId, getUserId, showAlertSnackBar } from 'utils/Global';
import {
  ProtectantAgentTitle,
  ProtectantAgentType,
  actionButtonBaseProps,
  actionButtonStyle,
} from './constants';
import { useCurrentWell } from 'redux/hooks/crystal';
import {
  checkProtectantAgentsUsage,
  getHideModalHandler,
  isProtectantAgentInUse,
} from './utils';
import { ModalDetails } from 'business/models/CustomModels';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useLastSelectedWellNavigationDetails } from 'redux/hooks/experiment';

export const useFetchSoakings = () => {
  const dispatch = useRMDispatch();

  const loadAllSoakings = async () => {
    const soakings = await SoakingService.getInstance().getAllSoakingOfCurrentUser();
    dispatch(setAllSoakings(soakings));
  };

  useEffect(() => {
    loadAllSoakings();
  }, []);
};

export const useDeleteSoakingHandler = (
  setSelectedRows: (selectedRows: number[]) => void,
) => {
  const dispatch = useRMDispatch();

  return async (soakingsToDelete: SoakingDTO[]) => {
    await SoakingService.getInstance().deleteSoakings(soakingsToDelete);
    dispatch(deleteSoakings(soakingsToDelete));
    setSelectedRows([]);
  };
};

export const useGetSoakingActionButtons = (
  soakingList,
  setSoakingList,
  selectedRows,
  setSelectedRows,
  editId,
  setEditId,
  setModalDetails,
) => {
  const addProtectantAgentDeleteConfirmation = useAddProtectantAgentDeleteConfirmation(
    ProtectantAgentType.Soaking,
    soakingList,
    selectedRows,
    setSelectedRows,
    setModalDetails,
  );

  const editProtectantAgentHandler = useEditProtectantAgentHandler(
    ProtectantAgentType.Soaking,
    selectedRows,
    setEditId,
    soakingList,
    setModalDetails,
  );
  return [
    {
      iconProps: { name: 'Edit', ...actionButtonBaseProps },
      isDisabled: selectedRows.length !== 1 || editId != null,
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: '#006af5',
      },
      onClick: editProtectantAgentHandler,
      titleTip: 'Edit',
    },
    {
      iconProps: { name: 'Add', ...actionButtonBaseProps },
      contentContainerStyle: actionButtonStyle,
      isDisabled: editId != null,
      onClick: () => {
        const _soakingList = [...soakingList];
        const soaking = new SoakingDTO();
        _soakingList.push(soaking);
        setSoakingList(_soakingList);
        setEditId(_soakingList.length - 1);
      },
      titleTip: 'Add',
    },
    {
      iconProps: { name: 'Delete', ...actionButtonBaseProps },
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: '#da1e28',
      },
      isDisabled: !selectedRows.length || editId != null,
      onClick: addProtectantAgentDeleteConfirmation,
      titleTip: 'Delete',
    },
  ] as RMCustomButtonProps[];
};

export const useGetCurrentWellIngredients = () => {
  const exp = useRMSelector(selectExp);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const { plateID, wellNum } = wellNavigationDetails || {};
  const [userId, setUserId] = React.useState<number | null>(null);

  useEffect(() => {
    getUserId().then((id) => {
      setUserId(id);
    });
  }, []);

  const ingredients = React.useMemo(() => {
    if (!userId) {
      return [];
    }
    const plate = exp.plateDto.find((pl) => pl.plateId === plateID);
    const well = plate.ingredients ? plate.ingredients.wellList[wellNum - 1] : null;
    return (
      well?.wellLayerList
        ?.map((layer) =>
          layer.wellLayerIngredientList?.map((element) => {
            const cryo = new CryoDTO();
            cryo.id = getNewId();
            cryo.userId = userId;
            cryo.ingredient = element.ingredientStockIngredient?.longName;
            cryo.conc = element.concentration;
            cryo.concUnit = element.ingredientStock?.concentrationUnits;
            cryo.pH = element.pH;
            cryo.sourceWellId = well.wellId;
            return cryo;
          }),
        )
        .flat() ?? []
    );
  }, [userId, exp, plateID, wellNum]);

  return ingredients;
};

export const useSaveNUpdateCryoprotectantsHandler = () => {
  const dispatch = useRMDispatch();
  return async (cryoProtectants) => {
    const newCryos: CryoDTO[] =
      await CryoprotectantService.getInstance().addCryoprotectants(cryoProtectants);
    dispatch(updateCryoprotectants(newCryos));
  };
};
export const useAddCryoprotectantsFromCurrentWellHandler = (setModalDetails) => {
  const currentWellIngredients: CryoDTO[] = useGetCurrentWellIngredients();
  const currentWell = useCurrentWell();
  const savedCryoprotectants = useCryoprotectants();
  const saveCryoHandler = useSaveNUpdateCryoprotectantsHandler();
  const hideModalHandler = getHideModalHandler(setModalDetails);
  return async () => {
    const isWellIngredientsPresent = savedCryoprotectants.some(
      (ing: CryoDTO) => ing.sourceWellId === currentWell?.wellId,
    );
    if (currentWellIngredients?.length && !isWellIngredientsPresent) {
      setModalDetails((prev) => ({
        ...prev,
        showModal: true,
        body: `Are you sure you want to add the listed ingredients below as cryoprotectant ingredients?\n${currentWellIngredients
          .map(
            (cryo: CryoDTO, index) =>
              `${index + 1}) ${cryo.ingredient}, ${cryo.conc}${cryo.concUnit}${
                cryo.pH ? `, ${cryo.pH}` : ''
              }`,
          )
          .join('\n')}`,
        heading: 'Load Ingredient from current well',
        onOk: () => {
          saveCryoHandler(currentWellIngredients);
          hideModalHandler();
          showAlertSnackBar(
            EAlertType.SUCCESS,
            ESnackBarPosition.TOP,
            'Load ingredient from current well success',
          );
        },
        isOkOnly: false,
        okButtonText: 'CONFIRM',
      }));
    } else {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        isWellIngredientsPresent
          ? 'ingredients from current well already added'
          : 'No ingredient found in the current well',
      );
    }
  };
};

export const useFetchCryoprotectants = () => {
  const dispatch = useRMDispatch();

  const loadAllCryoprotectants = async () => {
    const cryos =
      await CryoprotectantService.getInstance().getAllCryoprotectantsOfCurrentUser();
    dispatch(setAllcryoprotectants(cryos));
  };

  useEffect(() => {
    loadAllCryoprotectants();
  }, []);
};

export const useGetProtectantAgentDeleteHandler = (
  agentType,
  setSelectedRows,
  hideModalHandler,
) => {
  const deleteCryosHandler = useDeleteCryosHandler(setSelectedRows);
  const deleteSoakingHandler = useDeleteSoakingHandler(setSelectedRows);

  return (filteredAgent) => () => {
    if (agentType === ProtectantAgentType.Cryoprotectant) {
      deleteCryosHandler(filteredAgent as CryoDTO[]);
    } else {
      deleteSoakingHandler(filteredAgent as SoakingDTO[]);
    }
    hideModalHandler();
  };
};

export const useEditProtectantAgentHandler = (
  agentType,
  selectedRows,
  setEditId,
  agentList,
  setModalDetails,
) => {
  const hideModalHandler = getHideModalHandler(setModalDetails);
  return async () => {
    if (await isProtectantAgentInUse(agentList[selectedRows[0]], agentType)) {
      setModalDetails((prev) => ({
        ...prev,
        showModal: true,
        body: `You can't edit this ${ProtectantAgentTitle[
          agentType
        ].toLowerCase()} because the ingredient is used in the crystal cart.`,
        heading: `Edit ${ProtectantAgentTitle[agentType]}`,
        onOk: hideModalHandler,
        isOkOnly: true,
        okButtonText: 'CLOSE',
      }));
    } else {
      setEditId(selectedRows[0]);
    }
  };
};

export const useAddProtectantAgentDeleteConfirmation = (
  agentType: ProtectantAgentType,
  ProtectantAgentList: (CryoDTO | SoakingDTO)[],
  selectedRows: number[],
  setSelectedRows: React.Dispatch<React.SetStateAction<number[]>>,
  setModalDetails: React.Dispatch<React.SetStateAction<ModalDetails>>,
) => {
  const hideModalHandler = getHideModalHandler(setModalDetails);
  const protectantAgentDeleteHandler = useGetProtectantAgentDeleteHandler(
    agentType,
    setSelectedRows,
    hideModalHandler,
  );
  return async () => {
    const selectedProtectantAgents = selectedRows.map((item) => {
      return ProtectantAgentList[item];
    });

    const { error, filteredAgent } = await checkProtectantAgentsUsage(
      agentType,
      selectedProtectantAgents,
    );
    const areAllItemsBeingUsedInCrystals = error && filteredAgent.length == 0;
    if (error) {
      setModalDetails((prev) => ({
        ...prev,
        showModal: true,
        body: error,
        heading: areAllItemsBeingUsedInCrystals ? 'Error' : 'Warning',
        onOk: areAllItemsBeingUsedInCrystals
          ? hideModalHandler
          : protectantAgentDeleteHandler(filteredAgent),
        isOkOnly: areAllItemsBeingUsedInCrystals ? true : false,
        okButtonText: areAllItemsBeingUsedInCrystals ? 'CLOSE' : 'PROCEED',
      }));
    } else {
      setModalDetails((prev) => ({
        ...prev,
        showModal: true,
        body: `Are you sure you want to delete the following items\n${selectedProtectantAgents
          .map(
            (agent, index) =>
              `${index + 1}) ${agent.ingredient}, ${agent.conc}${agent.concUnit}${
                'pH' in agent && agent?.pH ? `, pH ${agent?.pH}` : ''
              }${
                'time' in agent && agent?.time
                  ? `, ${agent?.time} ${agent?.timeUnit}`
                  : ''
              }`,
          )
          .join('\n')}`,
        heading: 'Warning',
        onOk: protectantAgentDeleteHandler(selectedProtectantAgents),
        okButtonText: 'PROCEED',
        isOkOnly: false,
      }));
    }
  };
};

export const useDeleteCryosHandler = (
  setSelectedRows: (selectedRows: number[]) => void,
) => {
  const dispatch = useRMDispatch();

  return async (CryosToDelete: CryoDTO[]) => {
    await CryoprotectantService.getInstance().deleteCryoprotectants(CryosToDelete);
    dispatch(deleteCryoprotectants(CryosToDelete));
    setSelectedRows([]);
  };
};

export const useGetCryoActionButtons = (
  cryoList,
  setCryoList,
  selectedRows,
  setSelectedRows,
  editId,
  setEditId,
  setModalDetails,
) => {
  const AddCryoFormCurrentWellHandler =
    useAddCryoprotectantsFromCurrentWellHandler(setModalDetails);

  const addProtectantAgentDeleteConfirmation = useAddProtectantAgentDeleteConfirmation(
    ProtectantAgentType.Cryoprotectant,
    cryoList,
    selectedRows,
    setSelectedRows,
    setModalDetails,
  );
  const editProtectantAgentHandler = useEditProtectantAgentHandler(
    ProtectantAgentType.Cryoprotectant,
    selectedRows,
    setEditId,
    cryoList,
    setModalDetails,
  );

  return [
    {
      iconProps: {
        name: 'AddIngredient',
        ...actionButtonBaseProps,
      },
      contentContainerStyle: actionButtonStyle,
      isDisabled: editId != null,
      onClick: AddCryoFormCurrentWellHandler,
      titleTip: 'Add Well Ingredient',
    },
    {
      iconProps: { name: 'Edit', ...actionButtonBaseProps },
      isDisabled: selectedRows.length !== 1 || editId != null,
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: '#006af5',
      },
      onClick: editProtectantAgentHandler,
      titleTip: 'Edit',
    },
    {
      iconProps: { name: 'Add', ...actionButtonBaseProps },
      contentContainerStyle: actionButtonStyle,
      isDisabled: editId != null,
      onClick: () => {
        const _cryoList = [...cryoList];
        const cryo = new CryoDTO();
        _cryoList.push(cryo);
        setCryoList(_cryoList);
        setEditId(_cryoList.length - 1);
      },
      titleTip: 'Add',
    },
    {
      iconProps: { name: 'Delete', ...actionButtonBaseProps },
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: '#da1e28',
      },
      isDisabled: !selectedRows.length || editId != null,
      onClick: addProtectantAgentDeleteConfirmation,
      titleTip: 'Delete',
    },
  ] as RMCustomButtonProps[];
};

export const useSaveNUpdateSoakingsHandler = () => {
  const dispatch = useRMDispatch();
  return async (soakings) => {
    const newSoakings: SoakingDTO[] = await SoakingService.getInstance().addSoakings(
      soakings,
    );
    dispatch(updateSoakings(newSoakings));
  };
};
